import type {
  AIAnalysisResponse,
  AIAnalysisListParams,
  AIAnalysisListResponse,
  AIStreamChunk,
  RateLimitResponse,
  RootCauseAnalysisRequest,
  FishDiagnosisRequest,
  FollowUpRequest,
  FishSpecies,
} from '../types'

// ConnectRPC base URL - requests go to /ai.v1.AIService/* paths directly
// In development, Vite proxies these to the backend
// In production, Cloudflare Worker routes them to Fly.io backend
const CONNECT_BASE_URL = ''

// Helper to get auth token from cookie
function getAuthHeader(): string {
  // In cookie-based auth, the browser sends cookies automatically
  // But for ConnectRPC we may need to pass the token explicitly
  // This is a placeholder - adjust based on your auth implementation
  return ''
}

// Create ConnectRPC envelope for streaming requests
// Format: 1 byte flags + 4 byte big-endian length + JSON payload
function createEnvelope(data: unknown): ArrayBuffer {
  const json = JSON.stringify(data)
  const jsonBytes = new TextEncoder().encode(json)
  const envelope = new ArrayBuffer(5 + jsonBytes.length)
  const view = new DataView(envelope)

  // Flags byte (0x00 = no compression)
  view.setUint8(0, 0x00)
  // Message length (big-endian)
  view.setUint32(1, jsonBytes.length, false)
  // Copy JSON bytes
  new Uint8Array(envelope, 5).set(jsonBytes)

  return envelope
}

// ConnectRPC JSON streaming helper
async function* streamConnectRPC<TReq, TRes>(
  method: string,
  request: TReq,
  farmId?: string
): AsyncGenerator<TRes> {
  const url = `${CONNECT_BASE_URL}/ai.v1.AIService/${method}`

  // For ConnectRPC streaming, we need:
  // - Content-Type: application/connect+json (Connect protocol with JSON encoding)
  // - Connect-Protocol-Version: 1
  // - Request body must be enveloped (5 byte prefix + JSON)
  const headers: Record<string, string> = {
    'Content-Type': 'application/connect+json',
    'Connect-Protocol-Version': '1',
  }

  const authHeader = getAuthHeader()
  if (authHeader) {
    headers['Authorization'] = authHeader
  }

  if (farmId) {
    headers['X-Farm-ID'] = farmId
  }

  // Create enveloped request body
  const envelopedBody = createEnvelope(request)

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: envelopedBody,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Response body is not readable')
  }

  // Buffer to accumulate bytes for envelope parsing
  let buffer = new Uint8Array(0)

  try {
    while (true) {
      const { done, value } = await reader.read()

      if (done) break

      // Append new data to buffer
      const newBuffer = new Uint8Array(buffer.length + value.length)
      newBuffer.set(buffer)
      newBuffer.set(value, buffer.length)
      buffer = newBuffer

      // Parse enveloped messages from buffer
      // Each message: 1 byte flags + 4 bytes length (big-endian) + JSON payload
      while (buffer.length >= 5) {
        // First byte is flags (reserved for future use, e.g. compression)
        // Skip it and read the length from bytes 1-4
        const length = (buffer[1] << 24) | (buffer[2] << 16) | (buffer[3] << 8) | buffer[4]

        // Check if we have the full message
        if (buffer.length < 5 + length) break

        // Extract the JSON payload
        const jsonBytes = buffer.slice(5, 5 + length)
        const jsonStr = new TextDecoder().decode(jsonBytes)

        // Remove processed bytes from buffer
        buffer = buffer.slice(5 + length)

        try {
          const message = JSON.parse(jsonStr)
          // Check for Connect error in the envelope
          if (message.error) {
            console.error('Connect error:', message.error)
            throw new Error(message.error.message || 'Stream error')
          }
          // Yield the result (or the message itself if no result wrapper)
          yield (message.result || message) as TRes
        } catch (e) {
          if (e instanceof Error && e.message !== 'Stream error') {
            console.error('Failed to parse streaming chunk:', jsonStr, e)
          } else {
            throw e
          }
        }
      }
    }

    // Process any remaining complete messages in buffer
    while (buffer.length >= 5) {
      const length = (buffer[1] << 24) | (buffer[2] << 16) | (buffer[3] << 8) | buffer[4]
      if (buffer.length < 5 + length) break

      const jsonBytes = buffer.slice(5, 5 + length)
      const jsonStr = new TextDecoder().decode(jsonBytes)
      buffer = buffer.slice(5 + length)

      try {
        const message = JSON.parse(jsonStr)
        if (message.error) {
          throw new Error(message.error.message || 'Stream error')
        }
        yield (message.result || message) as TRes
      } catch (e) {
        console.error('Failed to parse final chunk:', jsonStr, e)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// Non-streaming ConnectRPC call
async function callConnectRPC<TReq, TRes>(
  method: string,
  request: TReq,
  farmId?: string
): Promise<TRes> {
  const url = `${CONNECT_BASE_URL}/ai.v1.AIService/${method}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Connect-Protocol-Version': '1',
  }

  const authHeader = getAuthHeader()
  if (authHeader) {
    headers['Authorization'] = authHeader
  }

  if (farmId) {
    headers['X-Farm-ID'] = farmId
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response.json()
}

// ==============================================================================
// AI Analysis API
// ==============================================================================

export const aiApi = {
  // Root Cause Analysis (non-streaming)
  analyzeRootCause: async (
    farmId: string,
    request: RootCauseAnalysisRequest
  ): Promise<AIAnalysisResponse> => {
    return callConnectRPC<RootCauseAnalysisRequest, AIAnalysisResponse>(
      'AnalyzeRootCause',
      {
        pondId: request.pondId,
        triggerRecordType: request.triggerRecordType,
        triggerRecordId: request.triggerRecordId,
        description: request.description,
        lookbackDays: request.lookbackDays,
      },
      farmId
    )
  },

  // Root Cause Analysis (streaming)
  analyzeRootCauseStream: (
    farmId: string,
    request: RootCauseAnalysisRequest
  ): AsyncGenerator<AIStreamChunk> => {
    return streamConnectRPC<RootCauseAnalysisRequest, AIStreamChunk>(
      'AnalyzeRootCauseStream',
      {
        pondId: request.pondId,
        triggerRecordType: request.triggerRecordType,
        triggerRecordId: request.triggerRecordId,
        description: request.description,
        lookbackDays: request.lookbackDays,
      },
      farmId
    )
  },

  // Fish Health Diagnosis (non-streaming)
  diagnoseFishHealth: async (
    farmId: string,
    request: FishDiagnosisRequest
  ): Promise<AIAnalysisResponse> => {
    return callConnectRPC<FishDiagnosisRequest, AIAnalysisResponse>(
      'DiagnoseFishHealth',
      {
        pondId: request.pondId,
        inputMode: request.inputMode,
        imageIds: request.imageIds,
        symptoms: request.symptoms,
        description: request.description,
        fishSpecies: request.fishSpecies,
        affectedCount: request.affectedCount,
        duration: request.duration,
      },
      farmId
    )
  },

  // Fish Health Diagnosis (streaming)
  diagnoseFishHealthStream: (
    farmId: string,
    request: FishDiagnosisRequest
  ): AsyncGenerator<AIStreamChunk> => {
    return streamConnectRPC<FishDiagnosisRequest, AIStreamChunk>(
      'DiagnoseFishHealthStream',
      {
        pondId: request.pondId,
        inputMode: request.inputMode,
        imageIds: request.imageIds,
        symptoms: request.symptoms,
        description: request.description,
        fishSpecies: request.fishSpecies,
        affectedCount: request.affectedCount,
        duration: request.duration,
      },
      farmId
    )
  },

  // Follow-up Question (non-streaming)
  askFollowUp: async (request: FollowUpRequest): Promise<AIAnalysisResponse> => {
    return callConnectRPC<FollowUpRequest, AIAnalysisResponse>(
      'AskFollowUp',
      {
        analysisId: request.analysisId,
        question: request.question,
      }
    )
  },

  // Follow-up Question (streaming)
  askFollowUpStream: (request: FollowUpRequest): AsyncGenerator<AIStreamChunk> => {
    return streamConnectRPC<FollowUpRequest, AIStreamChunk>(
      'AskFollowUpStream',
      {
        analysisId: request.analysisId,
        question: request.question,
      }
    )
  },

  // Get single analysis
  getAnalysis: async (analysisId: string): Promise<AIAnalysisResponse> => {
    return callConnectRPC<{ analysisId: string }, AIAnalysisResponse>(
      'GetAnalysis',
      { analysisId }
    )
  },

  // List analyses
  listAnalyses: async (params: AIAnalysisListParams): Promise<AIAnalysisListResponse> => {
    return callConnectRPC<AIAnalysisListParams, AIAnalysisListResponse>(
      'ListAnalyses',
      {
        farmId: params.farmId,
        pondId: params.pondId,
        analysisType: params.analysisType,
        anomalyCategory: params.anomalyCategory,
        status: params.status,
        startDate: params.startDate,
        endDate: params.endDate,
        cursor: params.cursor,
        limit: params.limit,
      }
    )
  },

  // Delete analysis
  deleteAnalysis: async (analysisId: string): Promise<{ success: boolean }> => {
    return callConnectRPC<{ analysisId: string }, { success: boolean }>(
      'DeleteAnalysis',
      { analysisId }
    )
  },

  // Check rate limit
  checkRateLimit: async (): Promise<RateLimitResponse> => {
    return callConnectRPC<Record<string, never>, RateLimitResponse>(
      'CheckRateLimit',
      {}
    )
  },

  // Get fish species
  getFishSpecies: async (activeOnly: boolean = true): Promise<{ species: FishSpecies[] }> => {
    return callConnectRPC<{ activeOnly: boolean }, { species: FishSpecies[] }>(
      'GetFishSpecies',
      { activeOnly }
    )
  },
}

export default aiApi
